import { css } from '@emotion/react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@adflow/frontend/ui-form';
import { theme, typography } from '@adflow/frontend/ui-theme';

const styles = {
	container: css({ display: 'flex', alignItems: 'center', gap: 16 }),
	checkboxWarning: css(typography.inputHelper, {
		color: theme.colors.text.secondary,
		display: 'flex',
		marginLeft: '0.75rem',
		marginTop: '-0.25rem',
	}),
	checkboxWrapper: css({
		display: 'flex',
		flexDirection: 'row',
		columnGap: '0.75rem',
		alignItems: 'flex-start',
	}),
	checkboxLabel: (disabled?: boolean) =>
		css(disabled && { color: theme.colors.text.disabled }),
};

export interface SaveColumnTemplateCheckboxProps {
	savedColumnTemplatesCount: number;
	savedAllColumnTemplatesCount: number;

	saved: boolean;
	savedAll: boolean;
	savedDefault: boolean;
	setSaved: (value: boolean) => void;
	setSavedAll: (value: boolean) => void;
	setSavedDefault: (value: boolean) => void;

	canSaveAll: boolean;
	canEdit: boolean;
}

const defaultMaxSavedColumnTemplates = 30;

export function SaveColumnTemplateCheckboxes({
	saved,
	savedAll,
	savedDefault,
	setSaved,
	setSavedAll,
	setSavedDefault,
	savedColumnTemplatesCount,
	savedAllColumnTemplatesCount,
	canSaveAll,
	canEdit,
}: SaveColumnTemplateCheckboxProps) {
	const { t } = useTranslation('common');

	return (
		<div css={styles.container}>
			<LabeledCheckbox
				checked={saved}
				onChange={() => {
					if (saved) {
						setSavedAll(false);
						setSavedDefault(false);
					}
					setSaved(!saved);
				}}
				disabled={
					savedColumnTemplatesCount >= defaultMaxSavedColumnTemplates ||
					!canEdit
				}
				label={t('columnTemplate.checkbox.saved')}
			/>

			<LabeledCheckbox
				checked={savedDefault}
				onChange={() => {
					if (!saved) setSaved(true);
					setSavedDefault(!savedDefault);
				}}
				label={t('columnTemplate.checkbox.savedDefault')}
			/>

			{canSaveAll && savedAllColumnTemplatesCount !== undefined ? (
				<LabeledCheckbox
					checked={savedAll}
					onChange={() => {
						if (!saved) setSaved(true);
						setSavedAll(!savedAll);
					}}
					disabled={
						savedAllColumnTemplatesCount >= defaultMaxSavedColumnTemplates ||
						!canEdit ||
						!canSaveAll
					}
					label={t('columnTemplate.checkbox.savedAll')}
				/>
			) : null}
		</div>
	);
}

function LabeledCheckbox({
	checked,
	onChange,
	disabled,
	label,
}: {
	checked: boolean;
	onChange: () => void;
	disabled?: boolean;
	label: string;
}) {
	const checkboxId = useId();
	return (
		<div css={styles.checkboxWrapper}>
			<Checkbox
				checked={checked}
				onChange={onChange}
				id={checkboxId}
				disabled={disabled}
				css={{ marginTop: 2 }}
			/>
			<label htmlFor={checkboxId} css={styles.checkboxLabel(disabled)}>
				{label}
			</label>
		</div>
	);
}
